"use client";

import { useLocale } from "@i18n/react";
import type { DefaultError, UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { useOpenApiClient } from "../../../providers/OpenApiProvider";
import { useSession } from "../../../session/hooks/useSession";
import type { paths } from "../../generated/schema";

type Paths = keyof paths;
export type GetPaths = {
  [Path in Paths]: paths[Path] extends { get: unknown } ? Path : never;
}[Paths];
export type GetParameters<Path extends GetPaths> = paths[Path]["get"] extends {
  parameters: infer Parameters;
}
  ? Parameters
  : undefined;
export type GetResponse<Path extends GetPaths> = paths[Path]["get"] extends {
  responses: {
    200: { content: { "application/json": infer Response } };
  };
}
  ? Response
  : unknown;

export type UseOpenApiQueryOptions<
  Path extends GetPaths,
  TData = GetResponse<Path>,
> = Omit<
  Partial<UseQueryOptions<GetResponse<Path>, DefaultError, TData>>,
  "queryFn"
> & {
  urlBackendPath: Path;
  params?: GetParameters<Path>;
  enabledWithoutSessionToken?: boolean;
};

export const useOpenApiQuery = <
  Path extends GetPaths,
  TData = GetResponse<Path>,
>({
  enabled = true,
  enabledWithoutSessionToken = false,
  params,
  queryKey,
  urlBackendPath,
  ...options
}: UseOpenApiQueryOptions<Path, TData>) => {
  const { openApiClient } = useOpenApiClient();
  const locale = useLocale();
  const { token } = useSession();

  return useQuery({
    enabled:
      (enabledWithoutSessionToken ? true : typeof token === "string") &&
      enabled,
    queryKey: queryKey ?? [urlBackendPath, params, locale],
    queryFn: async () => {
      const { data, error } = await openApiClient.GET(urlBackendPath, {
        ...(params !== undefined ? { params } : {}),
      } as never);
      if (error) throw error;
      return data as GetResponse<Path>;
    },
    ...options,
  });
};
